import React, {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { CopilotAppApis } from './types';

interface CopilotMethodsContextValue {
  deleteChatHistory: () => void;
  sendContentToCopilot: (payload: {
    contentName: string;
    contentId: string;
  }) => void;
  registerApis: (apis: CopilotAppApis) => void;
}

const CopilotMethodsContext = createContext<
  CopilotMethodsContextValue | undefined
>(undefined);

interface CopilotMethodsProviderProps {
  children: React.ReactNode;
  openVisibilityWithDefaultMessage: (message?: string) => void;
  isVisible: boolean;
  visitorContactId?: string | number | null;
}

const isVisitorContactIdPresent = (
  id: CopilotMethodsProviderProps['visitorContactId']
): id is string | number => id != null && id !== '';

export const CopilotMethodsProvider: React.FC<CopilotMethodsProviderProps> = ({
  children,
  openVisibilityWithDefaultMessage,
  isVisible,
  visitorContactId,
}) => {
  const copilotApisRef = useRef<CopilotAppApis | null>(null);

  const registerApis = useCallback((apis: CopilotAppApis) => {
    copilotApisRef.current = apis;
  }, []);

  const deleteChatHistory = useCallback(() => {
    copilotApisRef.current?.triggerResetChat?.();
  }, []);

  const sendContentToCopilot = useCallback(
    (payload: { contentName: string; contentId: string }) => {
      const message = `@@mention:${payload.contentId}@@`;
      if (copilotApisRef.current?.updateInputMessage && isVisible) {
        return copilotApisRef.current.updateInputMessage(message, true);
      }
      openVisibilityWithDefaultMessage(message);
    },
    [openVisibilityWithDefaultMessage, isVisible]
  );

  const value = useMemo(
    () => ({
      deleteChatHistory,
      sendContentToCopilot,
      registerApis,
    }),
    [deleteChatHistory, sendContentToCopilot, registerApis]
  );

  if (!isVisitorContactIdPresent(visitorContactId)) {
    return <>{children}</>;
  }

  return (
    <CopilotMethodsContext.Provider value={value}>
      {children}
    </CopilotMethodsContext.Provider>
  );
};

export const useCopilotMethods = (): CopilotMethodsContextValue | null =>
  useContext(CopilotMethodsContext) ?? null;
